/**
 * Kyrgyzstan-only replacements for Russian copy (manastender.com, 2026-11-04).
 *
 * The same mechanism as `ru.kz.ts`: `i18n/index.ts` lays this map over `ru` when
 * — and only when — `countryConfig.code === 'kg'`. Neither the Uzbek nor the
 * Kazakh build reads it.
 *
 * It overrides EVERY key `ru.kz.ts` overrides (each one is a sentence that is only
 * true in Uzbekistan) with its Kyrgyz truth, and then the keys where Kyrgyzstan
 * differs from Kazakhstan too: yearly plans are sold, a yearly plan can be paid by
 * invoice (USD, SWIFT), the company id is a 14-digit ИНН, the classifier ОКГЗ,
 * money «сом», Bishkek UTC+6. The same two rules as `ru.kz.ts` hold (checked by
 * `kg-overrides.test.ts`): a key here must exist in `ru.ts`, and may use only the
 * placeholders the `ru.ts` value uses.
 *
 * ⚠️ A FIRST PASS written before the site launched. Copy that names portals,
 * methods or law is for the owner to read before the first ad goes out.
 */
export const ruKg: Record<string, string> = {
  // ── Coverage lines of the Pro card ──────────────────────────────────────
  'plans.feature.allTenders': 'Все госзакупки {{countryGen}}: три государственных портала',
  'plans.feature.privateTenders': 'Закупки госпредприятий и муниципалитетов (zakupki.okmot.kg)',
  'plans.feature.ifi': 'Тендеры МФИ: Всемирный банк, АБР, ЕБРР, ООН',
  // ── What the catalogue is ───────────────────────────────────────────────
  'region.intro':
    'Государственные тендеры и закупки в {{region}}: сейчас {{count}} активных. По каждому доступны бюджет, срок подачи заявок и краткое описание на русском языке. Фильтруйте по категории, сохраняйте поиск и получайте новые тендеры на почту или в Telegram.',
  'category.intro':
    '«{{category}}» — одно из направлений государственных закупок в {{countryPrep}}. Сейчас в каталоге {{count}} актуальных тендеров этой категории: по каждому указаны начальная сумма, срок подачи заявок и краткое описание на русском языке. Уточните регион в каталоге, сохраните поиск и получайте новые закупки на почту или в Telegram.',
  'seo.catalog.description':
    'Каталог государственных тендеров и госзакупок {{countryGen}}: актуальные закупки, краткие описания на русском языке, фильтры по региону и категории, оповещения на почту и в Telegram — на {{brand}}.',
  'about.lead':
    '{{brand}} — это единый каталог государственных закупок {{countryGen}}. Мы собираем тендеры с государственных порталов в одном месте, добавляем разбор закупок на русском языке и помогаем поставщикам быстрее находить подходящие госконтракты.',
  'about.feature.ai.desc':
    'Краткое описание по большинству тендеров на русском языке — даже если объявление опубликовано на кыргызском.',
  'about.feature.alerts.title': 'Оповещения в Telegram и на почту',
  'about.sources.body':
    'Мы агрегируем публичные данные государственных порталов закупок {{countryGen}} (goszakupki.okmot.kg, zakupki.gov.kg, zakupki.okmot.kg) и международных организаций. {{brand}} — независимый сервис и не является государственным порталом.',
  'ifi.page.intro':
    'Закупки по проектам, финансируемым международными институтами. Заказчик — государственное ведомство {{countryGen}}, финансирование банковское, поэтому бюджет публикуется не в {{currency}}, а в валюте проекта.',
  'result.hint.ifi':
    'Проекты, финансируемые международными институтами (Всемирный банк, АБР и др.). Бюджет в {{currency}} не публикуется, поэтому в счёт входят открытые нотисы без суммы.',
  'ifi.seo.title': 'Тендеры международных финансовых институтов и агентств ООН в {{countryPrep}}',
  'ifi.seo.description':
    'Открытые закупки по проектам международных финансовых институтов в {{countryPrep}}: Всемирный банк, АБР, ЕБРР, ИБР, агентства ООН. Сроки, документы, требования.',
  // /directory: the KG categories (`kg-category.map.ts`) and the 9 regions.
  'directory.seoDesc':
    'Указатель всех разделов каталога госзакупок {{countryGen}}: тендеры по направлениям закупки и по 9 регионам, с числом активных тендеров в каждом.',
  // No supply-request source on KG; these only keep a stray deep link honest.
  'supply.page.intro':
    'Заявки на поставку без объявленного бюджета — они вынесены в отдельный раздел и не участвуют в основном каталоге.',
  'supply.seo.title': 'Запросы предложений без объявленного бюджета',
  'supply.seo.description': 'Заявки на поставку товаров и услуг без указания бюджета.',
  // «сом» beside a number fits narrow blocks (three letters, like «сум»).
  'tender.budget.unit': 'сом',
  'tender.openOriginalCoop': 'Открыть оригинал на портале',
  'saved.includeSupplyHint': 'Заявки без объявленного бюджета по этому фильтру.',
  'buyer.settlement.source': 'Источник: открытые данные о договорах',
  'tender.reveal.limitedDay':
    'Лимит открытия контактов на сегодня исчерпан ({{limit}}). Он снимется в {{time}} по времени Бишкека. Если это ошибка — напишите в поддержку.',

  // ── Alerts: e-mail or Telegram ──────────────────────────────────────────
  'search.empty.body':
    'Подходящего лота сейчас в каталоге нет. Подпишитесь — пришлём уведомление на почту или в Telegram в день публикации.',
  'tender.gate.guest':
    'Регистрируйтесь бесплатно — сохранённые поиски и оповещения на почту или в Telegram',
  'onboarding.niche.desc':
    'Мы будем присылать на почту или в Telegram каждый новый подходящий тендер по этой нише.',
  'onboarding.categories.desc':
    'Новые тендеры и оповещения на почту или в Telegram по выбранной нише.',
  'catalog.nudge.compact':
    'Выберите категорию и сохраните — новые тендеры придут на почту или в Telegram.',
  'tender.bidPackage.preparing': 'Готовим документ, пришлём на почту',
  'tender.bidPackage.sendHint':
    'Если файл не скачивается — встроенный браузер приложения так умеет, — заберите его почтой.',

  // ── /plans ──────────────────────────────────────────────────────────────
  'plans.social': 'Тендеры и закупки для поставщиков {{countryGen}}',
  'plans.seoDesc':
    'Тарифы {{brand}}: Pro — документы тендеров, цены победителей и аналитика по госзакупкам {{countryGen}}. Бесплатный доступ к описаниям и требованиям.',
  'plans.subtitle':
    'Документы тендеров, цены победителей, экспорт в Excel, оповещения о новых тендерах на почту и в Telegram.',
  'plans.feature.categoryNiche': '1 ниша + оповещения на почту или в Telegram',
  'plans.feature.savedSearchOne': 'До 3 сохранённых поисков + оповещения на почту или в Telegram',
  'plans.feature.categoryLimitUnlimited': 'Неограниченно категорий ОКГЗ',
  'plans.paymentVia': 'Оплата картой',
  'plans.trust.secureText': 'Карту обрабатывает Stripe — мы не видим и не храним её номер',
  'plans.trust.receiptTitle': 'Квитанция об оплате',
  'plans.trust.receiptText': 'После оплаты квитанция приходит на вашу почту',
  'plans.payOnceLead': 'Оплата на стороне Stripe · карту вводить у нас не нужно',
  'plans.payWithPayme': 'Оплатить картой',
  'plans.payWithClick': 'Оплатить картой',
  'plans.payOnceViaPayme': 'Оплатить картой',
  'plans.payWithCard': 'Оплатить картой (Visa/Mastercard)',
  'plans.clickCards': 'Visa · Mastercard',
  'plans.paymeOnlyNote': 'Помесячная оплата — только онлайн, банковской картой.',
  'plans.octo.tabOcto': 'Stripe',
  'plans.octo.tabPayme': 'Банковская карта',
  'plans.octo.continue': 'Продолжить на Stripe',
  'plans.octo.redirectNote': 'Данные карты вводятся на защищённой странице Stripe',
  'plans.tinRequired': 'Для этого тарифа нужен ИНН компании. Укажите его — и вернитесь к оплате.',
  // ── What is sold: Stripe monthly or yearly; a yearly plan also by invoice ──
  'plans.trust.noSurpriseText':
    'Подписка на месяц или на год: напоминание перед каждым списанием, продление отключается в любой момент',
  'plans.bannerPair': 'Pro и Бизнес · месяц или год',
  'requisites.note':
    'Платежи банковской картой за подписку проходят через платёжную систему Stripe. Годовую подписку компания может оплатить по счёту — переводом в долларах США (SWIFT).',
  'billing.return.failedBody':
    'Банк отклонил платёж или оплата была отменена — подписка не активирована. Можно попробовать ещё раз: той же картой или другой.',
  'phone.subtitle': 'Свяжемся по вопросам подписки. Звонков по рекламе не будет.',
  'billing.autoRenew.card': 'Карта в Stripe: {{pan}}',
  'billing.autoRenew.note':
    'Списание через Stripe в день окончания периода, без подтверждения. Отключить можно в любой момент.',
  'billing.autoRenew.retention':
    'Пока продление включено, доступ не прерывается. Выключить — одно нажатие: подписка не продлится и закончится {{date}}.',
  // ── Card sheet ──────────────────────────────────────────────────────────
  'billing.card.subtitle': 'Данные карты обрабатывает Stripe. Мы карту не храним.',
  'billing.card.subscribeSubtitle': 'Карту мы не храним — данные обрабатывает Stripe.',
  // The base line («Отключение в два клика…») sits under Stripe's pay button: no «клик» here.
  'billing.card.cancelHow': 'Отключение в два нажатия в «Тарифах».',
  'billing.card.receiptPayme': 'Квитанция об оплате придёт на почту',
  'billing.card.receiptOcto': 'Квитанция об оплате будет доступна в истории платежей',
  'billing.history.fiscalReceipt': 'Квитанция',
  'billing.card.nextHint': 'Дальше банк может запросить код подтверждения.',
  'billing.card.smsFromPayme': 'Код подтверждения — от вашего банка, номер карты мы не видим',
  'billing.card.securedByPay': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByOcto': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByClick': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByPayClick': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.smsFrom':
    'Код подтверждения придёт от вашего банка (не от {{brand}}) на номер, привязанный к карте.',
  'billing.card.otpSubtitle': 'Введите код подтверждения от банка.',
  'billing.card.smsSentTo': 'Банк отправил код подтверждения на {{phone}}.',
  'billing.card.smsSentGeneric': 'Банк отправил код подтверждения на номер, привязанный к карте.',
  'billing.card.errorTinRequired':
    'Для этого тарифа нужен ИНН компании. Укажите его в настройках и повторите оплату — код подтверждения здесь ни при чём.',
  'billing.card.errorCorporateCard':
    'Эта корпоративная карта не принимается. Используйте другую карту.',
  'billing.card.errorCurrency': 'Валюта карты не подходит. Попробуйте другую карту.',
  'billing.card.errorUnsupported':
    'Такая карта не поддерживается. Проверьте номер: принимаются Visa и Mastercard.',
  'billing.card.errorUnsupportedOcto':
    'Эта карта не поддерживается. Используйте карту Visa или Mastercard.',
  'billing.card.payViaOcto': 'Оплатить другой картой',
  'billing.card.foreignBrandHint': 'Принимаются карты Visa и Mastercard.',
  'billing.card.corporateOcto': 'Корпоративные карты не принимаются.',

  // ── Company id: a 14-digit ИНН (the word is the same as UZ's) ──────────
  // The operator's own ИНН in the footer is Uzbek: say whose it is, as KZ does.
  'footer.inn': 'ИНН (Узбекистан)',
  'support.companyData.topic': 'Тема: данные компании, ИНН {{tin}}',
  'support.companyData.prefill': 'Данные компании ИНН {{tin}}. ',
  'results.wall.line': 'Контакты, ИНН и суммы победителей — в Pro',
  'buyer.tin': 'ИНН',
  'org.tinNumber': 'ИНН {{number}}',
  'buyer.seoTitle': '«{{name}}» — ИНН {{tin}}: закупки и поставщики',
  'buyer.seoDescription':
    '{{name}}, ИНН {{tin}} — {{awards}} контрактов на {{value}}, {{suppliers}} поставщиков, {{active}} активных тендеров. Категории и суммы — {{brand}}.',
  'supplier.seoTitle': '«{{name}}» — ИНН {{tin}}: госзакупки, контракты, победы',
  'supplier.seoDescription':
    '{{name}}, ИНН {{tin}} — {{contracts}} госконтрактов на {{value}}, {{buyers}} заказчиков, {{period}}. Категории и регионы поставок — {{brand}}.',
  'invoiceFlow.tinLabel': 'ИНН вашей организации (14 цифр)',
  'invoiceFlow.errIssue': 'Не удалось отправить заявку. Проверьте ИНН и попробуйте ещё раз.',
  'companies.search': 'Название или ИНН',
  'tender.bidPackage.noCompany':
    'ИНН в профиле не указан, поэтому проверить требования по вашей компании мы не смогли — все пункты нужно проверить самостоятельно.',
  'freeUnlock.needTin': 'Добавьте ИНН компании',
  'company.card.notFound':
    'Мы не нашли компанию с таким ИНН среди участников закупок. Проверьте номер — или оставьте как есть.',
  'company.savedTitle': 'ИНН сохранён',
  'settings.summary.noCompany': 'ИНН не указан',
  'company.tinLabel': 'ИНН',
  'company.saved': 'ИНН сохранён.',
  'company.error.invalidTin': 'Введите {{digits}} цифр ИНН',
  'company.error.checksum': 'Номер не прошёл проверку — сверьте цифры ИНН',
  'company.error.changeLimit': 'ИНН сегодня меняли слишком часто. Попробуйте завтра.',
  'onboarding.inn.title': 'Название или ИНН вашей организации?',
  'company.title': 'Название или ИНН вашей организации?',
  'company.lookup.label': 'Название или ИНН вашей организации',
  'company.lookup.placeholder': 'Название или {{digits}} цифр ИНН',
  'company.lookup.emptyNoSkip': 'Не нашли по названию — введите ИНН',
  'company.lookup.tinValue': 'ИНН {{value}}',
  'claim.card.needsTin': 'Сначала укажите ИНН компании в разделе выше.',
  'claim.conflict.title': 'Этот ИНН уже привязан к другому аккаунту',

  // ── Classifier: ОКГЗ ────────────────────────────────────────────────────
  'results.card.inputLabel': 'Слова, категории и коды ОКГЗ',
  'results.card.ikpu': 'ОКГЗ',
  'results.change.ikpu': 'коды ОКГЗ',
  'savedSearches.instant.no.broad': 'Только для поисков с уточнением (слово, ОКГЗ, регион, ниша…)',
  'saved.edit.errorSubject': 'Оставьте хотя бы одно: категорию, ОКГЗ, нишу, заказчика или слово.',
  'saved.categoryAdd': 'Добавить категорию, ОКГЗ или нишу',
  // «подписка» is the paid plan only (owner's vocabulary, KZ lessons #6): a saved
  // search is a «поиск» in every label of its editor.
  'saved.ikpuLabel': 'Коды ОКГЗ поиска',
  'saved.categoriesLabel': 'Категории поиска',
  'saved.nicheLabel': 'Ниши поиска',
  'saved.edit.a11yDesc': 'Название, категории, исключения и минимальный бюджет поиска.',
  'saved.ikpuRemove': 'Убрать код ОКГЗ',
  'similarWinners.hint':
    'Компании, выигравшие закупки с тем же кодом ОКГЗ за последние 24 месяца. Доля — доля побед.',
  'similarWinners.basisExact': 'Код ОКГЗ {{code}}: {{awards}}, {{winners}} за 24 месяца',
  'similarWinners.basisClass':
    'Группа ОКГЗ {{code}} — по точному коду победителей мало: {{awards}}, {{winners}} за 24 месяца',

  // ── ИИ-онбординг ────────────────────────────────────────────────────────
  'aiOnboarding.row.subject': 'Категории, ОКГЗ и ниши',
  'aiOnboarding.row.ikpu': 'Коды ОКГЗ',
  'aiOnboarding.row.keywords': 'Слова',
  // A som is ~1/6 of a tenge; the example floor is a typical small lot.
  'aiOnboarding.fill.placeholder':
    'нужны насосы и электродвигатели, Бишкек и Чуйская область, от 100 тыс.',
  // The KG ladder climbs the ОКГЗ tree (`proposal-widen.ts`, `codeLadder: 'okgz'`):
  // `class` = the code cut to 5 or 4 digits, `category` = the 2-digit division
  // (only with the proposal's words). The ru.ts wording («первые 6 цифр») is KZ's.
  'aiOnboarding.widened.class':
    'коды ОКГЗ — до группы (первые 4–5 цифр): придут и соседние позиции того же товара',
  'aiOnboarding.widened.category':
    'коды ОКГЗ — до раздела (первые 2 цифры): придут лоты раздела со словами из подбора',

  // ── Misc ────────────────────────────────────────────────────────────────
  'register.phonePlaceholder': '+996 555 123 456',
  'register.phoneInvalid': 'Введите корректный номер, например +996 555 123 456',
  'tender.bidWaitlistForm.tier1Range': 'до 20 млн {{currency}}',
  // ⚠️ Not measured — no KG lot is in a database yet. Re-check against the live
  // median after the first week, as KZ did on 2026-10-18.
  'saved.minBudgetPlaceholder': '100 000',
  'saved.maxBudgetPlaceholder': '1 000 000',
  'onboarding.sample.meta': '💰 12 000 000 {{currency}} · ⏰ осталось 12 дней',
  'plans.subscribeWithCard': 'Оплатить {{name}}',
  'plans.pageLead':
    'Читать и разбирать тендеры можно бесплатно. Тариф открывает документы и оригинал лота, цены победителей и историю контрактов, аналитику по заказчикам — то, что нужно, чтобы участвовать.',
  'plans.title': 'Все тендеры Кыргызстана, документы и аналитика',
  'plans.seoTitle': 'Тарифы и цены — Pro на ManasTender',
  // «клик» is a word Meta's ad moderation flags (KazTender, 22.10) — KZ lessons #46.
  'plans.feature.contactsAll': 'Оригинал тендера на площадке — одним нажатием',
  'plans.feature.telegramBot':
    'Telegram-бот: поиск, ИИ-ответы, дайджесты, управление сохранёнными поисками',
  'shell.upgrade.desc': 'Документы и оригиналы лотов, цены победителей, аналитика',
  'settings.summary.planFreeHint': 'Документы и оригиналы лотов закрыты',

  // ── Kyrgyzstan only: the yearly invoice request (USD, SWIFT) ────────────
  'plans.payByAccount': 'Оплата по счёту (для компаний)',
  'plans.invoiceSub': 'Перевод в долларах США по SWIFT · только годовой тариф',
  'plans.yearAccessNote': 'Доступ на 12 месяцев после зачисления оплаты',
  'invoiceFlow.issue': 'Отправить заявку на счёт',
  'invoiceFlow.issuing': 'Отправляем…',
  'invoiceFlow.activationNote':
    'Доступ откроется после зачисления средств — обычно 1–3 банковских дня.',
};
